/**
 * EVO — xodimlar bo'limining API klienti.
 *
 * Server tomoni: `apps/api/src/modules/hr/employees.controller.ts` va
 * `payroll.controller.ts` (migratsiya 0037).
 */
import { api } from './api';
import type { Page } from './products';

export const EMPLOYEE_STATUSES = ['active', 'suspended', 'fired'] as const;
export type EmployeeStatus = (typeof EMPLOYEE_STATUSES)[number];

/** Serverdagi `contractState()` bilan AYNAN bir xil to'plam. */
export type ContractState = 'none' | 'active' | 'expired' | 'future' | 'open';

export const DUTY_KINDS = ['duty', 'obligation'] as const;
export type DutyKind = (typeof DUTY_KINDS)[number];

export interface DutyRow {
  readonly id: number;
  readonly kind: DutyKind;
  readonly title: string;
  readonly description: string | null;
  readonly kpiPlanId: number | null;
  readonly kpiPeriodStart: string | null;
  readonly kpiPeriodEnd: string | null;
  readonly isActive: boolean;
}

export interface EmployeeRow {
  readonly id: number;
  readonly employeeNo: string;
  readonly userId: number | null;
  readonly userEmail: string | null;
  readonly fullName: string;
  readonly phone: string | null;
  readonly position: string;
  readonly department: string | null;
  readonly branchId: number | null;
  readonly branchName: string | null;
  readonly hiredAt: string;
  readonly firedAt: string | null;
  readonly status: string;
  readonly contractNo: string | null;
  readonly contractFrom: string | null;
  readonly contractTo: string | null;
  readonly contractState: ContractState;
  /** Pul — MATN. Hech qachon `Number()` qilinmaydi. */
  readonly salaryAmount: string;
  readonly salaryCurrency: string;
  readonly notes: string | null;
}

export interface EmployeeDetail extends EmployeeRow {
  readonly duties: readonly DutyRow[];
  readonly obligations: readonly DutyRow[];
}

/**
 * `api.get` so'rov satrini `Record<string, primitive>` sifatida kutadi,
 * shuning uchun bu tur `interface` emas, `type`: TypeScript da faqat
 * `type` da yashirin indeks imzosi bo'ladi.
 */
export type EmployeeListParams = {
  readonly page?: number;
  readonly pageSize?: number;
  readonly q?: string;
  readonly status?: EmployeeStatus;
  readonly branchId?: number;
};

/** Ro'yxat so'rovining umumiy shakli — `api.get` bilan mos. */
export type ListParams = Readonly<Record<string, string | number | boolean | null | undefined>>;

export const employeeKeys = {
  all: ['employees'] as const,
  list: (p: EmployeeListParams) => ['employees', 'list', p] as const,
  one: (id: number) => ['employees', 'one', id] as const,
};

export function fetchEmployees(
  params: EmployeeListParams,
  signal?: AbortSignal,
): Promise<Page<EmployeeRow>> {
  return api.get<Page<EmployeeRow>>('/employees', { query: params, signal });
}

export function fetchEmployee(id: number, signal?: AbortSignal): Promise<EmployeeDetail> {
  return api.get<EmployeeDetail>(`/employees/${id}`, { signal });
}

// ---------------------------------------------------------------------------
// URL holati
// ---------------------------------------------------------------------------

/**
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ OCHILGAN XODIM MANZILDA TURADI                                           ║
 * ║                                                                          ║
 * ║ Ilgari kartochka `openId` degan `useState` da edi va `routes/index.tsx`  ║
 * ║ da bu marshrutda `validateSearch` umuman yo'q edi. Oqibati global        ║
 * ║ qidiruvda ko'rindi: xodim ismi topilardi, Enter esa RO'YXATNI ochardi    ║
 * ║ va odam uni qo'lda qayta izlardi.                                        ║
 * ║                                                                          ║
 * ║ Qidiruv (`q`) va holat filtri ATAYLAB URL ga chiqarilmadi: ular ekranni  ║
 * ║ VARAQLASH usuli, ulashiladigan holat emas — `routes/index.tsx` dagi eski ║
 * ║ izoh shu qismida haq. `id` esa boshqa toifadagi ma'lumot: u ANIQ bitta   ║
 * ║ yozuvni ko'rsatadi va aynan shu havola qilinadi.                         ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 */
export interface EmployeeSearch {
  readonly id?: number;
}

export function parseEmployeeSearch(search: Record<string, unknown>): EmployeeSearch {
  const id = Number(search.id);
  return Number.isInteger(id) && id > 0 ? { id } : {};
}

export interface CreateEmployeeInput {
  readonly employeeNo: string;
  readonly fullName: string;
  readonly position: string;
  readonly department?: string | null;
  readonly phone?: string | null;
  readonly branchId?: number | null;
  readonly hiredAt: string;
  readonly contractNo?: string | null;
  readonly contractFrom?: string | null;
  readonly contractTo?: string | null;
  readonly salaryAmount?: string;
  readonly salaryCurrency?: string;
}

export function createEmployee(input: CreateEmployeeInput): Promise<EmployeeDetail> {
  return api.post<EmployeeDetail>('/employees', input);
}

/** Ishdan bo'shatish — sana va sabab MAJBURIY (serverda ham, bazada ham). */
export const FIRE_REASON_MIN = 5;

export function fireEmployee(
  id: number,
  firedAt: string,
  reason: string,
): Promise<EmployeeDetail> {
  return api.post<EmployeeDetail>(`/employees/${id}/fire`, { firedAt, reason });
}

export function addDuty(
  id: number,
  input: { kind: DutyKind; title: string; description?: string | null },
): Promise<EmployeeDetail> {
  return api.post<EmployeeDetail>(`/employees/${id}/duties`, input);
}

// ---------------------------------------------------------------------------
// Oylik, avans, qarzdorlik
// ---------------------------------------------------------------------------

export interface AdvanceRow {
  readonly id: number;
  readonly docNo: string;
  readonly advanceDate: string;
  readonly employeeId: number;
  readonly fullName: string;
  readonly amount: string;
  readonly currencyCode: string;
  readonly amountBase: string;
  readonly status: string;
  readonly payrollRunId: number | null;
}

export interface DebtRow {
  readonly id: number;
  readonly debtDate: string;
  readonly employeeId: number;
  readonly fullName: string;
  readonly source: string;
  readonly reason: string;
  readonly amount: string;
  readonly currencyCode: string;
  readonly amountBase: string;
  readonly paidBase: string;
  readonly balanceBase: string;
  readonly status: string;
}

export interface PayrollRunRow {
  readonly id: number;
  readonly periodYear: number;
  readonly periodMonth: number;
  readonly status: string;
  readonly grossBase: string;
  readonly advanceBase: string;
  readonly deductionBase: string;
  readonly netBase: string;
}

export const payrollKeys = {
  runs: (p: ListParams) => ['payroll', 'runs', p] as const,
  advances: (p: ListParams) => ['payroll', 'advances', p] as const,
  debts: (p: ListParams) => ['payroll', 'debts', p] as const,
};

export function fetchPayrollRuns(
  params: ListParams,
  signal?: AbortSignal,
): Promise<Page<PayrollRunRow>> {
  return api.get<Page<PayrollRunRow>>('/payroll-runs', { query: params, signal });
}

export function fetchAdvances(
  params: ListParams,
  signal?: AbortSignal,
): Promise<Page<AdvanceRow>> {
  return api.get<Page<AdvanceRow>>('/employee-advances', { query: params, signal });
}

export function fetchDebts(
  params: ListParams,
  signal?: AbortSignal,
): Promise<Page<DebtRow>> {
  return api.get<Page<DebtRow>>('/employee-debts', { query: params, signal });
}

// ---------------------------------------------------------------------------
// Yozuv amallari
// ---------------------------------------------------------------------------

/** Avans — stavkadan oshsa server 422 beradi (pul chiqishidan OLDIN). */
export function createAdvance(input: {
  readonly employeeId: number;
  readonly cashAccountId: number;
  readonly amount: string;
  readonly notes?: string | null;
}): Promise<AdvanceRow> {
  return api.post<AdvanceRow>('/employee-advances', input);
}

export const DEBT_SOURCES = ['shortfall', 'damage', 'loan', 'other'] as const;
export type DebtSource = (typeof DEBT_SOURCES)[number];

/** Qarz manbasi provodkani ham belgilaydi — serverdagi izohga qarang. */
export function createDebt(input: {
  readonly employeeId: number;
  readonly source: DebtSource;
  readonly reason: string;
  readonly amount: string;
}): Promise<DebtRow> {
  return api.post<DebtRow>('/employee-debts', input);
}

export interface PayrollRunDetail extends PayrollRunRow {
  readonly lines: readonly {
    readonly id: number;
    readonly employeeId: number;
    readonly fullName: string;
    readonly employeeNo: string;
    readonly salaryBase: string;
    readonly commissionBase: string;
    readonly bonusBase: string;
    readonly grossBase: string;
    readonly advanceBase: string;
    readonly deductionBase: string;
    readonly netBase: string;
  }[];
}

export function runPayroll(input: {
  readonly legalEntityId: number;
  readonly branchId?: number | null;
  readonly periodYear: number;
  readonly periodMonth: number;
}): Promise<PayrollRunDetail> {
  return api.post<PayrollRunDetail>('/payroll-runs', input);
}

export function payPayroll(id: number, cashAccountId: number): Promise<PayrollRunDetail> {
  return api.post<PayrollRunDetail>(`/payroll-runs/${id}/pay`, { cashAccountId });
}

export function fetchPayrollRun(id: number, signal?: AbortSignal): Promise<PayrollRunDetail> {
  return api.get<PayrollRunDetail>(`/payroll-runs/${id}`, { signal });
}

/**
 * XATO BERILGAN AVANSNI QAYTARISH.
 *
 * Server uchta to'siqni qo'yadi va ular ekranda TUSHUNTIRILADI, chunki
 * foydalanuvchi rad javobini olganda nima qilishni bilishi kerak:
 * oylikda ushlangan avans (avval oylikni teskarilash), yopilgan kun
 * (kunni qayta ochish), 24 soatlik oyna (administratorga murojaat).
 */
export function reverseAdvance(id: number, reason: string): Promise<AdvanceRow> {
  return api.post<AdvanceRow>(`/employee-advances/${id}/reverse`, { reason });
}

/**
 * QARZNI HISOBDAN CHIQARISH — bu tuzatish emas, QAROR.
 *
 * Shuning uchun alohida ruxsat (`payroll.writeoff`, odatda faqat
 * rahbarda) va sabab majburiy: bir oydan keyin «kim va nima uchun
 * kechirdi?» degan savol javobsiz qolmasligi kerak.
 */
export function writeOffDebt(id: number, reason: string): Promise<DebtRow> {
  return api.post<DebtRow>(`/employee-debts/${id}/write-off`, { reason });
}

/**
 * OYLIK HISOBINI TESKARILASH — to'lov bilan BIRGA.
 *
 * Server bitta amalda: pulni kassaga qaytaradi, avanslarni «yopilmagan»
 * holatiga, qarzlarni esa «ochiq» holatiga tiklaydi va daftarga ikkita
 * ko'zgu provodka yozadi.
 */
export function reversePayrollRun(id: number, reason: string): Promise<PayrollRunRow> {
  return api.post<PayrollRunRow>(`/payroll-runs/${id}/reverse`, { reason });
}
